import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Lock, ArrowRight, ArrowUpRight, ShieldCheck, CheckCircle2, Copy, Check, LogOut, Menu, X, Download, ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";
import api, { API, fmtUSD, formatApiError } from "@/lib/api";
import {
  DEAL, BLOCKS, TERM_SHEET, SUMMARY, STRUCTURE, RISKS, CONTACT, DISCLAIMERS,
  COVERAGE, WORKED_EXAMPLE, TIMELINE, FAQ, OWNERSHIP, WATERFALL, SECURITY, ABOUT,
} from "@/lib/deal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";

const NAV = [
  { id: "summary", label: "Summary" },
  { id: "structure", label: "Structure" },
  { id: "protection", label: "Protection" },
  { id: "risk", label: "Risk" },
  { id: "commit", label: "Commit" },
];

const packetUrl = () => `${API}/site/packet.pdf?token=${encodeURIComponent(localStorage.getItem("portal_token") || localStorage.getItem("site_token") || "")}`;

const splitLines = (t) => (t || "").split("\n").map((l) => l.trim()).filter(Boolean);
const parsePairs = (t) => splitLines(t).map((l) => { const [k, ...r] = l.split("|"); return [k.trim(), r.join("|").trim()]; }).filter(([k, v]) => k && v);
const parseBlocks = (t) => splitLines(t).map((l) => { const [h, ...r] = l.split("|"); return { h: h.trim(), p: r.join("|").trim() }; }).filter((b) => b.h && b.p);

export default function Site({ dealId, onBack, onLock, onAdmin, preview }) {
  const [progress, setProgress] = useState({ committed: 0, goal: DEAL.goal, percent: 0, closed: false });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setProgress({ committed: 0, goal: 0, percent: 0, closed: false, deal_name: "" });
    api.get(`/public/progress?deal_id=${dealId || ""}`).then(({ data }) => setProgress(data)).catch(() => {});
  }, [dealId]);

  const deck = progress.deck || {};
  const hasDeck = !!(deck && (deck.target || deck.summary));
  const target = deck.target || DEAL.target;
  const monthlyRevenue = deck.monthly_revenue || DEAL.monthlyRevenue;
  const arBase = deck.ar_base || DEAL.arBase;
  const returnTxt = deck.return_pct
    ? (String(deck.return_pct).includes("%") ? deck.return_pct : `${deck.return_pct}% return`)
    : `${progress.return_pct || 30}% return`;
  const termTxt = deck.term || `${progress.term_days || 65} business days`;
  const summaryParas = deck.summary ? deck.summary.split("\n").filter((s) => s.trim()) : SUMMARY;
  const statsData = [
    { value: returnTxt.replace(/\s*return/i, ""), label: "Target Return", sub: `over ~${termTxt}` },
    { value: `${progress.term_days || 65}`, label: "Term", sub: "business days" },
    { value: fmtUSD(progress.goal), label: "Raise Target", sub: "total allocation" },
    { value: `${progress.percent || 0}%`, label: "Committed", sub: fmtUSD(progress.committed) },
  ];
  const termSheet = deck.term_sheet ? parsePairs(deck.term_sheet) : TERM_SHEET;
  const coverageRatio = deck.coverage_ratio || COVERAGE.ratio;
  const coverageText = deck.coverage_text || COVERAGE.text;
  const waterfallIntro = deck.waterfall_intro || WATERFALL.intro;
  const waterfallSteps = deck.waterfall ? parseBlocks(deck.waterfall) : WATERFALL.steps;
  const faqItems = deck.faq ? parsePairs(deck.faq).map(([q, a]) => ({ q, a })) : FAQ;

  const scrollTo = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };
  const navItems = preview ? NAV.filter((n) => n.id !== "commit") : NAV;

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* NAV */}
      <header className="sticky top-0 z-50 bg-background border-b border-black">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="mono text-sm font-semibold tracking-[0.18em]">
            CANIHAVEMONEY&nbsp;LLC
          </button>
          <nav className="hidden md:flex items-center gap-9">
            <button data-testid="nav-back-deals" onClick={onBack} className="inline-flex items-center gap-2 mono text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-black">
              <ArrowLeft size={14} /> Deals
            </button>
            {navItems.map((n) => (
              <button key={n.id} data-testid={`nav-${n.id}`} onClick={() => scrollTo(n.id)} className="mono text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-black tick-underline">
                {n.label}
              </button>
            ))}
            {!preview && (
            <a data-testid="download-packet-nav" href={packetUrl()} className="inline-flex items-center gap-2 mono text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-black">
              <Download size={14} /> Packet
            </a>
            )}
            <button data-testid="lock-btn" onClick={onLock} className="inline-flex items-center gap-2 mono text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-black">
              <LogOut size={14} /> Lock
            </button>
          </nav>
          <button className="md:hidden" onClick={() => setOpen(!open)}>{open ? <X size={20} /> : <Menu size={20} />}</button>
        </div>
        {open && (
          <div className="md:hidden border-t border-border bg-background px-6 py-4 flex flex-col gap-4">
            <button onClick={onBack} className="mono text-xs uppercase tracking-[0.2em] text-left">← Deals</button>
            {navItems.map((n) => (
              <button key={n.id} onClick={() => scrollTo(n.id)} className="mono text-xs uppercase tracking-[0.2em] text-left">{n.label}</button>
            ))}
            {!preview && <a href={packetUrl()} className="mono text-xs uppercase tracking-[0.2em] text-left">Download Packet</a>}
            <button onClick={onLock} className="mono text-xs uppercase tracking-[0.2em] text-left">Lock</button>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative grain overflow-hidden border-b border-black">
        <div className="max-w-6xl mx-auto px-6 py-20 sm:py-28 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="inline-flex items-center gap-2 border border-black px-3 py-1 mb-8">
              <Lock size={12} />
              <span className="mono text-[10px] uppercase tracking-[0.3em]">Confidential · Invitation Only</span>
            </div>
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] max-w-4xl">
              {progress.deal_name ? progress.deal_name : <>A Confidential<br />Investment<br />Opportunity.</>}
            </h1>
            <p className="mt-8 max-w-2xl text-lg sm:text-xl font-light leading-relaxed text-neutral-700">
              {hasDeck ? (
                <>Short-term, receivables-backed financing for {target} — ~{monthlyRevenue}/mo revenue and a ~{arBase} receivable base. Targeting a{" "}
                <span className="text-black font-medium">{returnTxt}</span> over ~<span className="text-black font-medium">{termTxt}</span>.</>
              ) : (
                <>Short-term, receivables-backed financing. Targeting a{" "}
                <span className="text-black font-medium">{returnTxt}</span> over ~<span className="text-black font-medium">{termTxt}</span> on a {fmtUSD(progress.goal)} allocation.</>
              )}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              {preview ? (
                <span data-testid="preview-badge" className="inline-flex items-center gap-2 border border-black px-4 h-14 mono text-xs uppercase tracking-[0.2em] text-muted-foreground"><Lock size={14} /> Confidential preview · read-only</span>
              ) : (
                <>
                  <Button data-testid="hero-commit-btn" onClick={() => scrollTo("commit")} className="rounded-none bg-black text-white hover:bg-white hover:text-black border border-black transition-colors h-14 px-8 mono text-xs uppercase tracking-[0.25em]">
                    {progress.closed ? "Join the Waitlist" : "Participate"} <ArrowRight className="ml-2" size={16} />
                  </Button>
                  <a data-testid="hero-download-packet" href={packetUrl()} className="inline-flex items-center rounded-none bg-transparent text-black hover:bg-black hover:text-white border border-black transition-colors h-14 px-8 mono text-xs uppercase tracking-[0.25em]">
                    <Download size={16} className="mr-2" /> Download Packet
                  </a>
                </>
              )}
            </div>
          </motion.div>

          <div className="mt-16 max-w-3xl">
            <div className="flex items-end justify-between mb-3">
              <span className="mono text-xs uppercase tracking-[0.25em] text-muted-foreground">Committed to date</span>
              <span className="mono text-xs uppercase tracking-[0.25em] text-muted-foreground">Goal {fmtUSD(progress.goal)}</span>
            </div>
            <div className="flex items-baseline gap-4 mb-4">
              <span className="text-4xl sm:text-5xl font-black tracking-tighter">{fmtUSD(progress.committed)}</span>
              <span className="mono text-sm text-muted-foreground">{progress.percent}% of raise{progress.closed ? " · Closed" : ""}</span>
            </div>
            <div className="h-2 w-full bg-muted overflow-hidden">
              <motion.div className="h-full bg-black" initial={{ width: 0 }} animate={{ width: `${progress.percent}%` }} transition={{ duration: 1.1 }} />
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-b border-black">
        <div className="max-w-6xl mx-auto grid grid-cols-2 lg:grid-cols-4">
          {statsData.map((s, i) => (
            <div key={s.label} className={`p-8 sm:p-10 border-black ${i % 2 === 0 ? "border-r" : ""} ${i < 2 ? "border-b lg:border-b-0" : ""} ${i < 3 ? "lg:border-r" : ""}`}>
              <div className="text-4xl sm:text-5xl font-black tracking-tighter">{s.value}</div>
              <div className="mt-3 mono text-xs uppercase tracking-[0.2em]">{s.label}</div>
              <div className="mono text-xs text-muted-foreground mt-1">{s.sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* COVERAGE */}
      <section className="border-b border-black bg-black text-white">
        <div className="max-w-6xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="mono text-xs uppercase tracking-[0.3em] text-neutral-400">Collateral Coverage</div>
            <div className="text-7xl sm:text-8xl font-black tracking-tighter mt-4">{coverageRatio}</div>
          </div>
          <p className="text-lg font-light leading-relaxed text-neutral-300">{coverageText}</p>
        </div>
      </section>

      <Section id="summary" num="01" title="Executive Summary">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-6">
            {summaryParas.map((p, i) => <p key={i} className="text-lg font-light leading-relaxed text-neutral-800">{p}</p>)}
          </div>
          <div className="border border-black p-8 h-fit bg-black text-white">
            <ShieldCheck size={28} className="mb-6" />
            <div className="mono text-xs uppercase tracking-[0.2em] text-neutral-400">Sponsor Alignment</div>
            <div className="text-4xl font-black tracking-tighter mt-3">{fmtUSD(DEAL.sponsorCommitment)}</div>
            <p className="mt-4 text-sm font-light leading-relaxed text-neutral-300">Ralph is personally committing {fmtUSD(DEAL.sponsorCommitment)} alongside invited investors.</p>
          </div>
        </div>
      </Section>

      <Section id="ownership" num="02" title="Ownership Structure">
        <p className="max-w-2xl text-lg font-light leading-relaxed text-neutral-800 mb-12">{OWNERSHIP.intro}</p>
        <div className="grid md:grid-cols-3 border-l border-t border-black">
          {OWNERSHIP.chain.map((c, i) => (
            <div key={c.h} className="border-r border-b border-black p-8">
              <div className="mono text-xs text-muted-foreground">0{i + 1}</div>
              <h3 className="text-xl font-semibold tracking-tight mt-3">{c.h}</h3>
              <p className="mt-3 font-light leading-relaxed text-neutral-700">{c.p}</p>
            </div>
          ))}
        </div>
        <div className="grid md:grid-cols-2 border-l border-t border-black mt-10">
          <div className="border-r border-b border-black p-8 bg-black text-white">
            <div className="mono text-xs uppercase tracking-[0.2em] text-neutral-400">{OWNERSHIP.reit.title}</div>
            <ul className="mt-5 space-y-3">{OWNERSHIP.reit.points.map((p) => <li key={p} className="flex gap-3 text-sm font-light text-neutral-200"><span>—</span>{p}</li>)}</ul>
          </div>
          <div className="border-r border-b border-black p-8">
            <div className="mono text-xs uppercase tracking-[0.2em] text-muted-foreground">{OWNERSHIP.operator.title}</div>
            <ul className="mt-5 space-y-3">{OWNERSHIP.operator.points.map((p) => <li key={p} className="flex gap-3 text-sm font-light text-neutral-700"><span>—</span>{p}</li>)}</ul>
          </div>
        </div>
        <p className="mono text-xs text-muted-foreground mt-6">{OWNERSHIP.note}</p>
      </Section>

      <Section id="returns" num="03" title="How the Return Works">
        <p className="max-w-2xl text-lg font-light leading-relaxed text-neutral-800 mb-10">{WORKED_EXAMPLE.intro}</p>
        <div className="border border-black max-w-2xl">
          {WORKED_EXAMPLE.rows.map(([k, v], i) => (
            <div key={i} className={`flex items-center justify-between gap-6 p-5 ${i !== WORKED_EXAMPLE.rows.length - 1 ? "border-b border-border" : ""} ${i === WORKED_EXAMPLE.rows.length - 1 ? "bg-black text-white" : ""}`}>
              <span className="font-light">{k}</span>
              <span className={`mono text-lg ${v === "—" ? "text-muted-foreground" : "font-semibold"}`}>{v}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section id="terms" num="04" title="Term Sheet" dark>
        <div className="border border-white/20">
          {termSheet.map(([k, v], i) => (
            <div key={k} className={`grid sm:grid-cols-2 ${i !== termSheet.length - 1 ? "border-b border-white/20" : ""}`}>
              <div className="p-5 mono text-xs uppercase tracking-[0.2em] text-neutral-400 sm:border-r border-white/20">{k}</div>
              <div className="px-5 pb-5 sm:py-5 font-light">{v}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section id="structure" num="05" title="Deal Structure & Repayment">
        <div className="grid md:grid-cols-2 gap-px bg-black border border-black">
          {STRUCTURE.map((s, i) => (
            <div key={s.h} className="bg-background p-8">
              <div className="mono text-xs text-muted-foreground">0{i + 1}</div>
              <h3 className="text-2xl font-semibold tracking-tight mt-3">{s.h}</h3>
              <p className="mt-3 font-light leading-relaxed text-neutral-700">{s.p}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section id="timeline" num="06" title="Deal Timeline" dark>
        <div className="grid md:grid-cols-5 gap-px bg-white/20 border border-white/20">
          {TIMELINE.map((t, i) => (
            <div key={t.h} className="bg-black p-6">
              <div className="mono text-xs text-neutral-500">0{i + 1}</div>
              <h3 className="text-lg font-medium tracking-tight mt-3">{t.h}</h3>
              <p className="mt-2 text-sm font-light leading-relaxed text-neutral-400">{t.p}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section id="security" num="07" title="Security & Recourse">
        {SECURITY.map((p, i) => <p key={i} className="text-lg font-light leading-relaxed text-neutral-800 mb-4 max-w-3xl">{p}</p>)}
      </Section>

      <Section id="protection" num="08" title="Downside Protection">
        <p className="max-w-2xl text-lg font-light leading-relaxed text-neutral-800 mb-12">{waterfallIntro}</p>
        <div className="relative">
          {waterfallSteps.map((s, i) => (
            <div key={s.h} className="relative pl-14 pb-10 last:pb-0">
              {i !== waterfallSteps.length - 1 && <span className="absolute left-[15px] top-9 bottom-0 w-px bg-black/20" />}
              <span className="absolute left-0 top-0 w-8 h-8 bg-black text-white mono text-xs flex items-center justify-center">{i + 1}</span>
              <h3 className="text-xl font-semibold tracking-tight">{s.h}</h3>
              <p className="mt-2 max-w-2xl font-light leading-relaxed text-neutral-700">{s.p}</p>
            </div>
          ))}
        </div>
        <p className="mono text-xs text-muted-foreground mt-8">{WATERFALL.note}</p>
      </Section>

      <Section id="risk" num="09" title="Risk Factors" dark>
        <div className="space-y-px">
          {RISKS.map((r) => (
            <div key={r.h} className="grid md:grid-cols-3 gap-6 py-6 border-b border-white/20">
              <h3 className="text-xl font-medium tracking-tight">{r.h}</h3>
              <p className="md:col-span-2 font-light leading-relaxed text-neutral-300">{r.p}</p>
            </div>
          ))}
        </div>
      </Section>

      {preview ? (
        <Section id="commit" num="10" title="Interested?">
          <p className="text-lg font-light leading-relaxed text-neutral-800 max-w-2xl">This is a confidential preview. To review full terms, receive the packet, and reserve an allocation, you'll need an investor account. Please contact Canihavemoney LLC to be added as an investor.</p>
        </Section>
      ) : (
        <Section id="commit" num="10" title={progress.closed ? "Raise Closed" : "Your Commitment"}>
          {progress.closed ? <WaitlistFlow /> : <CommitFlow dealId={dealId} onProgress={(p) => setProgress(p)} />}
        </Section>
      )}

      <Section id="faq" num="11" title="Frequently Asked">
        <Accordion type="single" collapsible className="max-w-3xl border-t border-black">
          {faqItems.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-b border-black">
              <AccordionTrigger className="text-left text-lg font-medium hover:no-underline py-6">{f.q}</AccordionTrigger>
              <AccordionContent className="font-light leading-relaxed text-neutral-700 text-base pb-6">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Section>

      {/* FOOTER */}
      <footer className="border-t border-black">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="grid md:grid-cols-3 gap-10">
            <div>
              <div className="mono text-sm font-semibold tracking-[0.18em]">{CONTACT.name}</div>
              <p className="mono text-xs text-muted-foreground mt-3">{CONTACT.address}</p>
              <p className="mono text-xs text-muted-foreground mt-1">{CONTACT.email}</p>
              <div className="mt-6 space-y-2">{ABOUT.map((p, i) => <p key={i} className="text-xs font-light leading-relaxed text-muted-foreground">{p}</p>)}</div>
            </div>
            <div className="md:col-span-2 space-y-3">
              {DISCLAIMERS.map((d, i) => <p key={i} className="text-xs font-light leading-relaxed text-muted-foreground">{d}</p>)}
            </div>
          </div>
          <div className="mt-12 pt-6 border-t border-border flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <span className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              © {new Date().getFullYear()} Canihavemoney LLC · Confidential · All rights reserved
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Section({ id, num, title, children, dark }) {
  return (
    <section id={id} className={`border-b scroll-mt-20 ${dark ? "bg-black text-white border-white/10" : "border-black"}`}>
      <div className="max-w-6xl mx-auto px-6 py-20 sm:py-28">
        <div className="flex items-center gap-4 mb-12">
          <span className={`mono text-xs ${dark ? "text-neutral-500" : "text-muted-foreground"}`}>[{num}]</span>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tighter">{title}</h2>
        </div>
        {children}
      </div>
    </section>
  );
}

function WaitlistFlow() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Enter your name");
    if (!email.trim()) return toast.error("Enter your email");
    setBusy(true);
    try {
      await api.post("/site/waitlist", { name: name.trim(), email: email.trim() });
      setDone(true);
      toast.success("You're on the waitlist");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (done) return (
    <div data-testid="waitlist-confirmation" className="border border-black bg-black text-white p-8 max-w-lg">
      <CheckCircle2 size={30} className="mb-4" />
      <h3 className="text-2xl font-black tracking-tighter">You're on the list</h3>
      <p className="mt-3 font-light text-neutral-300">This raise has reached its target. We'll reach out first if an allocation opens up or a new deal launches.</p>
    </div>
  );

  return (
    <form onSubmit={submit} data-testid="waitlist-form" className="max-w-md">
      <p className="text-lg font-light leading-relaxed text-neutral-800 mb-8">This raise has reached its $2M target and is now closed. Join the waitlist to be notified first about the next allocation or deal.</p>
      <Label className="mono text-xs uppercase tracking-[0.2em]">Full name</Label>
      <Input data-testid="waitlist-name" value={name} onChange={(e) => setName(e.target.value)} className="rounded-none border-black h-12 mt-2 mb-6 focus-visible:ring-0 focus-visible:border-black" placeholder="Jane Q. Investor" />
      <Label className="mono text-xs uppercase tracking-[0.2em]">Email</Label>
      <Input data-testid="waitlist-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-none border-black h-12 mt-2 focus-visible:ring-0 focus-visible:border-black" placeholder="jane@email.com" />
      <Button type="submit" data-testid="waitlist-submit" disabled={busy} className="mt-6 rounded-none bg-black text-white hover:bg-white hover:text-black border border-black transition-colors h-14 px-10 mono text-xs uppercase tracking-[0.25em]">
        {busy ? "Joining…" : "Join Waitlist"}
      </Button>
    </form>
  );
}

function CommitFlow({ dealId, onProgress }) {
  const [commitment, setCommitment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [amount, setAmount] = useState(null);
  const [custom, setCustom] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const refreshProgress = () => api.get(`/public/progress?deal_id=${dealId || ""}`).then(({ data }) => onProgress(data)).catch(() => {});

  useEffect(() => {
    const id = localStorage.getItem("commitment_id");
    if (!id) { setLoading(false); return; }
    api.get(`/site/commitments/${id}`)
      .then(({ data }) => setCommitment(data.commitment))
      .catch(() => localStorage.removeItem("commitment_id"))
      .finally(() => setLoading(false));
  }, []);

  const selectBlock = (v) => { setAmount(v); setCustom(""); };
  const onCustom = (raw) => {
    const digits = raw.replace(/[^0-9]/g, "");
    setCustom(digits);
    setAmount(digits ? parseInt(digits, 10) : null);
  };
  const valid = amount && amount > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Enter your full legal name");
    if (!valid) return toast.error("Enter a commitment amount");
    setSubmitting(true);
    try {
      const { data } = await api.post("/site/commitments", { name: name.trim(), amount, deal_id: dealId || "" });
      localStorage.setItem("commitment_id", data.commitment._id);
      setCommitment(data.commitment);
      refreshProgress();
      toast.success("Commitment recorded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="mono text-xs text-muted-foreground animate-pulse">Loading…</div>;
  if (commitment) return <Committed commitment={commitment} setCommitment={setCommitment} />;

  return (
    <form onSubmit={submit} data-testid="commit-form">
      <div className="mb-8 max-w-md">
        <Label htmlFor="commit-name" className="mono text-xs uppercase tracking-[0.2em]">Full legal name</Label>
        <Input id="commit-name" data-testid="commit-name" value={name} onChange={(e) => setName(e.target.value)} required
          className="rounded-none border-black h-12 mt-2 focus-visible:ring-0 focus-visible:border-black" placeholder="Jane Q. Investor" />
      </div>

      <Label className="mono text-xs uppercase tracking-[0.2em]">Select an allocation block</Label>
      <div className="grid md:grid-cols-3 border-l border-t border-black mt-3">
        {BLOCKS.map((b) => (
          <button type="button" key={b.amount} data-testid={`commit-block-${b.amount}`} onClick={() => selectBlock(b.amount)}
            className={`text-left border-r border-b border-black p-6 transition-colors ${amount === b.amount && !custom ? "bg-black text-white" : "hover:bg-muted"}`}>
            <div className={`mono text-[10px] uppercase tracking-[0.25em] ${amount === b.amount && !custom ? "text-neutral-400" : "text-muted-foreground"}`}>{b.label}</div>
            <div className="text-3xl font-black tracking-tighter mt-3">{fmtUSD(b.amount)}</div>
            <div className={`mono text-[11px] uppercase tracking-[0.15em] mt-4 ${amount === b.amount && !custom ? "text-neutral-400" : "text-muted-foreground"}`}>Returns</div>
            <div className="text-lg font-semibold flex items-center gap-1">{fmtUSD(b.ret)} <ArrowUpRight size={14} /></div>
          </button>
        ))}
      </div>

      <div className="mt-8 max-w-md">
        <Label htmlFor="custom-amount" className="mono text-xs uppercase tracking-[0.2em]">Or enter a custom amount</Label>
        <div className="relative mt-2">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 mono text-sm text-muted-foreground">$</span>
          <Input id="custom-amount" data-testid="commit-custom-amount" inputMode="numeric" value={custom} onChange={(e) => onCustom(e.target.value)}
            className="rounded-none border-black h-12 pl-8 focus-visible:ring-0 focus-visible:border-black" placeholder="e.g. 75000" />
        </div>
        <p className="mono text-[11px] mt-2 text-muted-foreground">
          {valid ? `Projected return ${fmtUSD(Math.round(amount * 1.3))}` : "Enter any amount"}
        </p>
      </div>

      <div className="mt-8 flex items-center gap-3 text-muted-foreground">
        <Lock size={14} />
        <span className="mono text-xs">Wire instructions appear right after you commit.</span>
      </div>

      <Button type="submit" data-testid="commit-submit" disabled={submitting}
        className="mt-6 rounded-none bg-black text-white hover:bg-white hover:text-black border border-black transition-colors h-14 px-10 mono text-xs uppercase tracking-[0.25em]">
        {submitting ? "Submitting…" : "Submit Commitment"}
      </Button>
    </form>
  );
}

function Committed({ commitment, setCommitment }) {
  const [wire, setWire] = useState(null);
  const [copied, setCopied] = useState("");
  const [marking, setMarking] = useState(false);
  const sent = commitment.status === "sent";

  useEffect(() => {
    api.get("/site/wire").then(({ data }) => setWire(data)).catch(() => {});
  }, []);

  const copy = (label, val) => {
    navigator.clipboard.writeText(val || "");
    setCopied(label);
    setTimeout(() => setCopied(""), 1500);
  };

  const markSent = async () => {
    setMarking(true);
    try {
      await api.post(`/site/commitments/${commitment._id}/sent`);
      setCommitment({ ...commitment, status: "sent" });
      toast.success("Marked as sent — thank you");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setMarking(false);
    }
  };

  const rows = wire
    ? [
        ["Bank Name", wire.bank_name],
        ["Account Name", wire.account_name],
        ["Account Number", wire.account_number],
        ["Routing Number", wire.routing_number],
        ["SWIFT / BIC", wire.swift],
        ["Bank Address", wire.bank_address],
        ["Reference", wire.reference],
        ["State of Formation", wire.state_of_formation],
      ].filter(([, v]) => v)
    : [];

  return (
    <div data-testid="commitment-confirmation">
      <div className="border border-black bg-black text-white p-8">
        <CheckCircle2 size={32} className="mb-5" />
        <h3 className="text-2xl font-black tracking-tighter">{sent ? "Wire Marked as Sent" : "Commitment Recorded"}</h3>
        <p className="mt-3 font-light text-neutral-300 max-w-lg">
          {sent
            ? "Thank you. Canihavemoney LLC will confirm receipt of your funds."
            : "Wire your funds using the instructions below, then mark the wire as sent."}
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-8 pt-8 border-t border-white/20">
          <KV k="Investor" v={commitment.investor_name} />
          <KV k="Allocation" v={fmtUSD(commitment.amount)} />
          <KV k="Projected Return" v={fmtUSD(commitment.return_amount)} />
          <KV k="Status" v={sent ? "Wire Sent" : "Awaiting Wire"} />
        </div>
      </div>

      <div className="mt-10">
        <div className="flex items-center gap-3 mb-5">
          <Lock size={16} />
          <h3 className="mono text-xs uppercase tracking-[0.25em]">Wire Instructions</h3>
        </div>
        <div data-testid="wire-instructions" className="border border-black">
          {rows.length === 0 && (
            <div className="p-5 mono text-xs text-muted-foreground">Wire instructions are being finalized by Canihavemoney LLC.</div>
          )}
          {rows.map(([k, v], i) => (
            <div key={k} className={`flex items-center justify-between gap-4 p-4 ${i !== rows.length - 1 ? "border-b border-border" : ""}`}>
              <div>
                <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{k}</div>
                <div className="mono text-sm mt-1">{v}</div>
              </div>
              <button onClick={() => copy(k, v)} className="text-muted-foreground hover:text-black shrink-0">{copied === k ? <Check size={16} /> : <Copy size={16} />}</button>
            </div>
          ))}
          {wire?.notes && <div className="p-4 border-t border-border bg-muted mono text-xs text-muted-foreground">{wire.notes}</div>}
        </div>

        {!sent ? (
          <Button data-testid="mark-sent-btn" onClick={markSent} disabled={marking}
            className="mt-6 rounded-none bg-black text-white hover:bg-white hover:text-black border border-black transition-colors h-14 px-10 mono text-xs uppercase tracking-[0.25em]">
            {marking ? "Saving…" : "I've Sent the Wire"} <Check className="ml-2" size={16} />
          </Button>
        ) : (
          <p data-testid="sent-confirmation" className="mt-6 inline-flex items-center gap-2 mono text-xs uppercase tracking-[0.2em]">
            <CheckCircle2 size={16} /> Wire marked as sent
          </p>
        )}
      </div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div>
      <div className="mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">{k}</div>
      <div className="text-lg font-semibold mt-1">{v}</div>
    </div>
  );
}
